'use client';

import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  WEAPON_ICON_SRC, GRENADE_ICON_SRC, SMOKE_ICON_SRC, FLASH_ICON_SRC, KNIFE_ICON_SRC, MEDKIT_ICON_SRC,
} from '../spriteLayout';
import { FaCrosshairs, FaShieldAlt, FaBolt, FaUsers, FaCoins, FaGem, FaClock, FaHandshake, FaSignOutAlt, FaTrophy, FaLightbulb } from '../../icons';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import translations, { SEARCH_TIPS } from './RushMid.translations';
import { AtlasIcon } from './shared/AtlasIcon';

const Img: FC<{ src: string; size?: number }> = ({ src, size = 26 }) => (
  <AtlasIcon src={src} size={size} className="select-none" />
);

export const TIP_ICONS: ReactNode[] = [
  <FaCrosshairs className="text-[#ff6a6a]" />,
  <Img src={KNIFE_ICON_SRC} />,
  <Img src={SMOKE_ICON_SRC} />,
  <Img src={FLASH_ICON_SRC} />,
  <Img src={SMOKE_ICON_SRC} />,
  <Img src={GRENADE_ICON_SRC} />,
  <Img src={MEDKIT_ICON_SRC} />,
  <FaUsers className="text-sky-300" />,
  <FaUsers className="text-sky-300" />,
  <FaShieldAlt className="text-[#5aa0ff]" />,
  <FaBolt className="text-[#f2b84b]" />,
  <FaShieldAlt className="text-[#5aa0ff]" />,
  <FaCoins className="text-price" />,
  <FaCrosshairs className="text-[#ff6a6a]" />,
  <FaCrosshairs className="text-[#ff6a6a]" />,
  <FaCoins className="text-price" />,
  <FaUsers className="text-sky-300" />,
  <Img src={KNIFE_ICON_SRC} />,
  <Img src={GRENADE_ICON_SRC} />,
  <Img src={WEAPON_ICON_SRC.awp} size={30} />,
  <Img src={WEAPON_ICON_SRC.lmg} size={30} />,
  <FaGem className="text-cyan-300" />,
  <Img src={KNIFE_ICON_SRC} />,
  <FaClock className="text-white/80" />,
  <FaHandshake className="text-white/80" />,
  <FaSignOutAlt className="text-accent-red" />,
  <FaTrophy className="text-price" />,
  <FaCrosshairs className="text-sky-300" />,
];

if (process.env.NODE_ENV !== 'production' && TIP_ICONS.length !== SEARCH_TIPS.en.length) {
  console.warn(`TIP_ICONS (${TIP_ICONS.length}) is out of sync with SEARCH_TIPS (${SEARCH_TIPS.en.length})`);
}

export const TipRow: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <div className="flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2 text-start">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center">{icon}</div>
    <p className="text-sm leading-snug text-white/90">{children}</p>
  </div>
);

export const RushMidSearchTip: FC = () => {
  const { t, lang } = useLocalTranslation(translations);
  const tips = SEARCH_TIPS[lang];
  const [i, setI] = useState(() => Math.floor(Math.random() * tips.length));
  const next = useCallback(() => {
    setI((n) => {
      if (tips.length <= 1) return n;
      let k = Math.floor(Math.random() * (tips.length - 1));
      if (k >= n) k += 1;
      return k;
    });
  }, [tips.length]);
  const timer = useRef(0);
  const arm = useCallback(() => {
    window.clearInterval(timer.current);
    timer.current = window.setInterval(next, 5500);
  }, [next]);
  useEffect(() => { arm(); return () => window.clearInterval(timer.current); }, [arm]);
  const advance = useCallback(() => { next(); arm(); }, [next, arm]);
  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-2">
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-side-t">
        <FaLightbulb size={14} /> {t('tipLabel')}
      </div>

      <button
        type="button"
        onClick={advance}
        className="w-full bg-transparent p-0 outline-none active:opacity-70"
        aria-label={t('tipLabel')}
      >
        <div key={i} className="animate-shot-tip-in">
          <TipRow icon={TIP_ICONS[i]}>{tips[i]}</TipRow>
        </div>
      </button>
    </div>
  );
};
